// src/pages/customer/CustomerDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCustomerData } from './useCustomerData';
import { useCart } from './useCart';

import OverviewPanel   from './customerpanel/OverviewPanel';
import BrowsePanel     from './customerpanel/BrowsePanel';
import OrdersPanel     from './customerpanel/OrdersPanel';
import WishlistPanel   from './customerpanel/WishlistPanel';
import InquiriesPanel  from './customerpanel/InquiriesPanel';
import ProfilePanel    from './customerpanel/ProfilePanel';
import CartPanel       from './customerpanel/CartPanel';

const NAV = [
  { key: 'overview',   icon: 'dashboard',     label: 'Overview'   },
  { key: 'browse',     icon: 'search',        label: 'Browse'     },
  { key: 'orders',     icon: 'receipt_long',  label: 'My Orders'  },
  { key: 'wishlist',   icon: 'favorite',      label: 'Wishlist'   },
  { key: 'inquiries',  icon: 'forum',         label: 'Inquiries'  },
  { key: 'profile',    icon: 'person',        label: 'Profile'    },
  { key: 'cart',       icon: 'shopping_cart', label: 'Cart'       },
];

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const { cart } = useCart(user);

  const [active, setActive] = useState(
    () => window.location.hash.replace('#', '') || 'overview'
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const setActiveTab = (tab) => {
    window.location.hash = tab;
    setActive(tab);
  };

  useEffect(() => {
    const onPopState = () => {
      if (!window.location.pathname.startsWith('/customer')) return;
      const tab = window.location.hash.replace('#', '') || 'overview';
      setActive(tab);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const {
    myOrders, myWishlist, myInquiries, stats, loading, error,
    fetchMyOrders, fetchMyWishlist, fetchMyInquiries,
    removeFromWishlist,
  } = useCustomerData(user?.id);

  // ── Auth guard ─────────────────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (user.role !== 'customer') navigate('/');
  }, [user, navigate, authLoading]);

  // ── Load data on tab change ────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authLoading) return;
    if (active === 'overview')  { fetchMyOrders(); fetchMyWishlist(); }
    if (active === 'orders')    { fetchMyOrders(); }
    if (active === 'wishlist')  { fetchMyWishlist(); }
    if (active === 'inquiries') { fetchMyInquiries(); }
  }, [active, authLoading, fetchMyOrders, fetchMyWishlist, fetchMyInquiries]);

  if (authLoading) return null;

  const pendingInquiries = myInquiries.filter(q =>
    q.status === 'open' || q.status === 'pending'
  ).length;

  const handleLogout = () => { logout(); navigate('/'); };

  // ── Panel map ──────────────────────────────────────────
  const PANELS = {
    overview:  <OverviewPanel
                 stats={stats}
                 myOrders={myOrders}
                 onNavigate={setActiveTab}
               />,

    browse:     <BrowsePanel
                  user={user}
                  onBuyNow={() => setActiveTab('cart')}
                />,

    orders:    <OrdersPanel
                  orders={myOrders}
                  loading={loading}
                  onRefresh={fetchMyOrders}
                  user={user}
                />,

    wishlist:  <WishlistPanel
                 wishlist={myWishlist}
                 loading={loading}
                 onRemove={removeFromWishlist}
               />,

    inquiries: <InquiriesPanel />,

    profile:   <ProfilePanel user={user} onUserUpdate={() => {}} />,
  };

  // ──────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen bg-[#f4f6f4] font-body">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ──────────────────────────────────── */}
      <aside className={`fixed left-0 top-0 h-screen w-60 bg-white border-r
        border-outline-variant/20 flex flex-col z-40 shadow-sm
        transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0`}>

        <div className="px-5 py-5 border-b border-outline-variant/10">
          <img src="/assets/elite-goats.png" alt="Elite Goats"
               className="h-10 w-auto object-contain" />
          <p className="text-[10px] font-bold text-outline uppercase tracking-widest mt-1">
            Customer Portal
          </p>
        </div>

        <nav className="flex-1 px-3 mt-4 space-y-0.5">
          {NAV.map((item) => (
            <button key={item.key} onClick={() => { setActiveTab(item.key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                          text-sm font-semibold transition-all ${
                active === item.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
              }`}>
              <span className="material-symbols-outlined text-[20px]">{item.icon}</span>
              <span className="flex-1 text-left">{item.label}</span>

              {/* Inquiries badge */}
              {item.key === 'inquiries' && pendingInquiries > 0 && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                  active === item.key
                    ? 'bg-white/20 text-white'
                    : 'bg-tertiary text-white'
                }`}>
                  {pendingInquiries}
                </span>
              )}

              {/* Wishlist badge */}
              {item.key === 'wishlist' && myWishlist.length > 0 && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                  active === item.key
                    ? 'bg-white/20 text-white'
                    : 'bg-primary/20 text-primary'
                }`}>
                  {myWishlist.length}
                </span>
              )}

              {/* Cart badge */}
              {item.key === 'cart' && cart.length > 0 && (
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                  active === item.key
                    ? 'bg-white/20 text-white'
                    : 'bg-primary text-white'
                }`}>
                  {cart.length}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-outline-variant/10 space-y-1">
          <button onClick={() => navigate('/', { state: { viewHome: true } })}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs
                       font-medium text-on-surface-variant
                       hover:bg-surface-container transition-all">
            <span className="material-symbols-outlined text-[18px]">home</span>
            Back to Site
          </button>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm
                       font-semibold text-red-600 hover:bg-red-50 transition-all">
            <span className="material-symbols-outlined text-[20px]">logout</span>
            Logout
          </button>
        </div>
      </aside>

      {/* ── Main ─────────────────────────────────────── */}
      <main className="flex-1 md:ml-60 min-h-screen pt-20 px-4 md:px-8 pb-8">

        {/* ── Top Header ───────────────────────────────── */}
        <header className="fixed top-0 left-0 md:left-60 right-0 z-40 h-16 bg-white/80
                          backdrop-blur-md border-b border-outline-variant/20 flex items-center
                          justify-between px-4 md:px-8 gap-4">
          <button className="md:hidden p-2 rounded-xl hover:bg-surface-container text-secondary"
                  onClick={() => setSidebarOpen(true)}>
            <span className="material-symbols-outlined text-[24px]">menu</span>
          </button>

          <div>
            <p className="text-sm font-black text-on-surface">
              {NAV.find(n => n.key === active)?.label}
            </p>
            <p className="text-[10px] text-secondary">
              Welcome back, {user?.name?.split(' ')[0] || 'Customer'} 👋
            </p>
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <button onClick={() => setActiveTab('browse')}
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-primary
                         text-white font-bold text-sm hover:opacity-90
                         transition-all shadow-sm">
              <span className="material-symbols-outlined text-[18px]">search</span>
              Browse Animals
            </button>

            <div className="h-7 w-px bg-outline-variant/40" />

            <div className="flex items-center gap-3">
              <div className="text-right hidden lg:block">
                <p className="text-sm font-bold text-on-surface leading-tight">
                  {user?.name || 'Customer'}
                </p>
                <p className="text-[10px] text-secondary uppercase tracking-wider">
                  Customer Account
                </p>
              </div>
              {user?.profile_image ? (
                <img src={user.profile_image} alt="avatar"
                    className="w-9 h-9 rounded-full object-cover border-2 border-primary/20" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-primary flex items-center
                                justify-center text-white font-black text-sm
                                border-2 border-primary/20">
                  {(user?.name || 'C')[0].toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ── Error banner ─────────────────────────────── */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700
                          px-4 py-3 rounded-xl text-sm flex items-center gap-2">
            <span className="material-symbols-outlined text-base">error</span>
            {error}
          </div>
        )}

        {/* ── Active panel ─────────────────────────────── */}
        {active === 'cart'
          ? <CartPanel onNavigate={setActiveTab} user={user} />
          : PANELS[active]
        }

      </main>
    </div>
  );
}